import type { TFunction } from 'i18next';
import type { MentionOption } from './mentionFilter';
import type { GradingPushMode } from './pushData';

/** Props for the GradingPushModal — confirms how grading-group lines are copied to target rows. */
export interface GradingPushModalProps {
  show: boolean;
  /** Apply the chosen grading-line mode to every target row. */
  onConfirm: (mode: GradingPushMode) => void;
  onCancel: () => void;
}

/** A single "grading group" sample line that can repeat under a product row. */
export interface GradingGroupLine {
  id: string;
  gradingGroup: string;
  /** Multi-select: sizes are scoped to the selected grading group's sizeRanges. */
  size: string[];
  length: string;
  quantity: string;
  uom: string;
}

/** A single "Core Product" line that can repeat under a product row (Costing Bulk Request). */
export interface CoreProductLine {
  id: string;
  /** Colourway / Size / Length are multi-select — one line can cover several of each. */
  colourway: string[];
  size: string[];
  length: string[];
  quantity: string;
  /** Price per unit — plain field for now; costing calculation is a follow-on. */
  pricePerUnit: string;
  /** Total price — plain field for now; auto-calc (quantity × pricePerUnit) is a follow-on. */
  totalPrice: string;
  /** Per-line radio selection (single per product row). */
  selected: boolean;
}

/** Field keys a costing gap can be reported against. Core Product keys are prefixed so they
 *  never collide with the Costing Information keys, which are plain `MultiSubmitRow` keys.
 *  Lives here rather than in costingPayload so the grid context can reference it without
 *  types.ts and costingPayload.ts importing each other. */
export type CostingErrorField =
  | 'ciCostingTemplate'
  | 'ciIncoterms'
  | 'ciShipmentMethod'
  | 'ciSampleRef'
  | 'core.quantity';

/** A grading group dropdown option carries its product's `sizeRanges` so the Size cell can scope its options.
 *  `sizeRanges` items can be plain strings or `{ name, isSampleSize }` objects depending on the source. */
export interface RawSizeRange {
  name: string;
  isSampleSize?: boolean;
}
export interface GradingGroupOption {
  value: string;
  label: string;
  sizeRanges?: Array<string | RawSizeRange>;
}

/** Sample type group as returned by getSampleTypeGroupsByProduct, with the raw sub-types we need. */
export interface RawSampleType {
  _id: string;
  name?: string;
  sampleType?: { _id: string; name: string };
}
export interface RawSampleTypeGroup {
  _id: string;
  sampleGroupName: string;
  isDefault?: boolean;
  sampleTypeAutomation?: Array<{ sampleType?: { _id: string; name: string }; configuration?: unknown[] }>;
}

/** Minimal shape of a product's existing samples — used for chain attachment (resolveNextSampleContext). */
export interface RawProductSample {
  _id: string;
  /** Human-readable Sample Ref number. */
  sampleNumber?: string;
  /** The Sample Ref as the Costing Information dropdown shows it: sample type, then sample number. */
  sampleRefLabel?: string;
  sampleType?: string | { _id: string };
  sampleTypeObj?: { _id: string };
  supplier?: string;
  parentId?: string | null;
  revisionRootId?: string | null;
  versionNumber?: number;
  /** The group this sample belongs to — the same field the Samples page list query filters on. */
  sampleTypeGroupId?: string | null;
  /** 'other' for material samples (OtherSamples master), 'main' otherwise. */
  typeOfSampleType?: string | null;
  /** false/undefined on rows predating the Sample Type Group feature, which belong to the default group. */
  isNewSample?: boolean;
}

/**
 * One product row in the Multi Submit grid. Holds every editable control's state
 * (static replica — all local, nothing is persisted to the backend).
 */
export interface MultiSubmitRow {
  id: string;
  productNumber: string;
  /** Colourway section checkbox — when checked, the selected colourways are submitted as payload colours. */
  cwChecked: boolean;
  /** Per-row colourway selection; submitted as payload colours when cwChecked. */
  productColourways: SelectOption[];
  /** Controlled row-select checkbox state. */
  selected: boolean;
  /** Row-level expand caret: when true the Request Sample grading lines stack vertically. */
  expanded: boolean;

  cpChecked: boolean;
  purchaseOrders: SelectOption[];

  tpDesignDetails: boolean;
  tpBom: boolean;
  tpSpecification: boolean;
  tpSpecificationValue: SelectOption[];
  tpTesting: boolean;
  tpCare: boolean;
  tpSample: boolean;
  tpSampleValue: SelectOption[];
  /** No/Yes pill — "combine technical package into one document". */
  tpCombineYes: boolean;

  sdDesignDetails: boolean;
  sdDesignDetailsValue: SelectOption[];
  sdColour: boolean;
  sdColourValue: SelectOption[];
  sdBom: boolean;
  sdBomValue: SelectOption[];
  sdSpecification: boolean;
  sdSpecificationValue: SelectOption[];
  sdPomGuide: boolean;
  sdPomGuideValue: SelectOption[];
  sdPurchaseOrder: boolean;
  sdPurchaseOrderValue: SelectOption[];
  sdTestingReport: boolean;
  sdCareLayout: boolean;

  rsChecked: boolean;
  sampleTypeGroup: string;
  /** 'main' | 'other' — derived from selected sampleTypeGroup (group → 'main', other-bucket → 'other'). */
  typeOfSampleType: '' | 'main' | 'other';
  sampleType: string;
  /** User-entered sample reference number. */
  sampleNumber: string;
  rsSupplier: string;
  dueDate: string;
  factory: string;
  colourway: string;
  gradingLines: GradingGroupLine[];
  /** Chain attachment computed from product's existing samples (set when sampleType selected). */
  parentId: string | null;
  revisionRootId: string | null;
  versionNumber: number;

  /** Costing Information group — all local (static replica; batch-details API wiring is a follow-on). */
  ciReference: string;
  ciSupplier: string;
  ciFactory: string;
  ciCostingTemplate: string;
  ciCurrency: string;
  ciMoqNeeded: string;
  ciTargetCost: string;
  ciIncoterms: string;
  ciShipmentMethod: string;
  ciSampleRef: string;

  /** Core Product group — stacked lines (mirrors the Request Sample grading stack). */
  coreLines: CoreProductLine[];

  /** Supplier invite toggled on this product row — when true, Submit sends the costing supplier invite. */
  invite: boolean;

  emailTo: string;
  emailCc: string;
  emailBcc: string;
  emailMessage: string;
}

/** Imperative callbacks handed to cell renderers through AG Grid's `context`. */
export interface MultiSubmitGridContext {
  updateRow: (id: string, patch: Partial<MultiSubmitRow>) => void;
  updateGradingLine: (rowId: string, lineId: string, patch: Partial<GradingGroupLine>) => void;
  addGradingLine: (rowId: string) => void;
  removeGradingLine: (rowId: string, lineId: string) => void;
  /** Patch a single Core Product line on a row. */
  updateCoreLine: (rowId: string, lineId: string, patch: Partial<CoreProductLine>) => void;
  /** Append a blank Core Product line (expands the row). */
  addCoreLine: (rowId: string) => void;
  toggleRowExpanded: (id: string) => void;
  /** Expand or collapse every expandable column group at once. */
  setAllGroupsExpanded: (open: boolean) => void;
  /** Expand/collapse every expandable product row at once (Product Number header caret). */
  toggleAllRowsExpanded: () => void;
  /** True when every expandable product row is currently expanded (drives the header caret state). */
  allRowsExpanded: boolean;
  /** True when at least one product row is expandable (>1 grading line); hides the header caret when none. */
  anyRowExpandable: boolean;
  toggleSelectAll: (checked: boolean) => void;
  /** True when every row is currently selected (drives the header checkbox). */
  allSelected: boolean;
  /** Id of the row chosen as the copy source (clicked product number), or null. */
  sourceRowId: string | null;
  /** Toggle a row as the copy source — re-clicking the current source clears it. */
  setSourceRow: (id: string) => void;
  /** Live supplier-invite flag for a row, read from current state (AG Grid leaves `data` stale for
   *  value-less cells, so the invite icon must read this rather than its own cell data). */
  isRowInvited: (id: string) => boolean;
  /** Navigate to a product's detail page (Product Identifier) by devOrderId. */
  openProductDetail: (id: string) => void;
  /** Toggle the Product Number column menu (three-dot): opens it, or closes it if already open. */
  toggleProductColumnMenu: () => void;
  /** Per-group header checkbox state, keyed by column-group id. */
  groupChecks: Record<string, boolean>;
  toggleGroupCheck: (groupId: string) => void;
  /** True while the Request Sample column group is open in the header. */
  isRequestSampleOpen: boolean;
  /** True while the Core Product column group is open in the header (drives core-line stacking). */
  isCoreProductOpen: boolean;
  /** All fetched per-product option lists, keyed by row id (devOrderId). */
  optionsByRow: Record<string, RowOptions>;
  /** Convenience accessor returning EMPTY_ROW_OPTIONS for unknown/loading rows. */
  optionsFor: (rowId: string) => RowOptions;
  /** Required costing fields still empty on a row, set on Submit. Empty for a clean row. */
  errorsFor: (rowId: string) => CostingErrorField[];
  /** Translation fn bound to the modules/multi-submit namespace, for cell-renderer strings. */
  t: TFunction;
  /** Page-level: supplier currently selected at the top filter — Request Sample supplier is locked to this. */
  selectedSupplierId: string;
  /** Page-level: full supplier list (for RS Supplier label lookup). */
  supplierOptions: SelectOption[];
  /** Costing Information supplier options — the single selected main (header) supplier only. */
  costingSupplierOptions: SelectOption[];
  /** Page-level: authenticated company's currency (read-only display in Costing Information). */
  companyCurrency: string;
  /** Supplier _id -> currency code; drives the Costing Information currency + Core Product price
   *  symbol from each row's selected Costing Information supplier (`ciSupplier`). */
  supplierCurrencyById: Record<string, string>;
  /** Page-level: factory list for the selected supplier. */
  factoryOptions: SelectOption[];
  /** Page-level: UoM master (global). */
  unitOptions: SelectOption[];
  /** Page-level: "main"-bucket sample types — the global master list (independent of selected group). */
  mainSamplesList: Array<{ _id: string; name: string }>;
  /** Page-level: "other"-bucket sample types — chosen when group selection is the synthetic "other" entry. */
  otherSamplesList: Array<{ _id: string; name: string }>;
  /** Page-level: master size list. */
  sizeOptions: SelectOption[];
  /** Page-level: master length list. */
  lengthOptions: SelectOption[];
  /** Page-level: active Incoterms master (Costing Information). */
  incotermsOptions: SelectOption[];
  /** Page-level: Costing Stage master (Costing Information). */
  /** Patch helper that also updates dependent chain-attachment fields when sampleTypeGroup/sampleType change. */
  setSampleTypeGroup: (rowId: string, groupId: string) => void;
  setSampleType: (rowId: string, sampleTypeId: string) => void;
  /** Company users available for `@` mentions in the Compose Email cells. */
  userMentions: MentionOption[];
  /** Queue one fill/paste cell write; every write queued in the same task is applied in one batched render. */
  queueCellWrite: (rowId: string, write: () => void) => void;
  /** Expand a product row so every grading and Core Product line shows; a no-op on an expanded row. */
  expandRow: (id: string) => void;
  /** Set a row's Sample Type from a fill or paste, resolving the id or label against the row's Sample
   *  Type Group as it is when the write lands, so a group pasted alongside it is the one checked. */
  fillSampleType: (rowId: string, idOrLabel: string) => void;
}

export type SelectOption = { value: string; label: string };

/** Per-product option lists fetched on page load, keyed by devOrderId in grid context. */
export interface RowOptions {
  poOptions: SelectOption[];
  gradingGroups: GradingGroupOption[];
  sampleOptions: SelectOption[];
  designDocs: SelectOption[];
  colourDocs: SelectOption[];
  bomDocs: SelectOption[];
  otherDocs: SelectOption[];
  poDocs: SelectOption[];
  colourwayOptions: SelectOption[];
  /** This product's own samples, as Sample Ref options for Costing Information. */
  sampleRefOptions: SelectOption[];
  /** Sample type groups available for this product (driven by product attributes). */
  sampleTypeGroupOptions: SelectOption[];
  /** Raw group docs (carry sampleTypeAutomation) used to derive per-row sample-type options. */
  sampleTypeGroupRaw: RawSampleTypeGroup[];
  /** Product's existing samples — used to resolve chain attachment (parentId/revisionRootId). */
  samplesList: RawProductSample[];
  /** Per-product leg-length options (DevelopmentOrders.legLengths populated with name). */
  legLengthOptions: SelectOption[];
  /** Core Product per-product colourway options (from the core-product-details batch). */
  coreColourwayOptions: SelectOption[];
  /** Core Product per-product size options (from the core-product-details batch). */
  coreSizeOptions: SelectOption[];
  /** Core Product per-product length options (from the core-product-details batch). */
  coreLengthOptions: SelectOption[];
  /** Costing templates filtered for this product's dev order — mirrors product-costing-detail/add. */
  costingTemplateOptions: SelectOption[];
  /** Costing Information factory options — the selected supplier's factories that this product actually
   *  selected (DevelopmentOrders.supplierFactories ∩ supplier factory master). Empty when the product
   *  has no factory selected, so its Costing Information Factory dropdown offers nothing. */
  ciFactoryOptions: SelectOption[];
}

/** Empty option set used before a row's options have loaded. */
export const EMPTY_ROW_OPTIONS: RowOptions = {
  poOptions: [],
  gradingGroups: [],
  sampleOptions: [],
  designDocs: [],
  colourDocs: [],
  bomDocs: [],
  otherDocs: [],
  poDocs: [],
  colourwayOptions: [],
  sampleRefOptions: [],
  sampleTypeGroupOptions: [],
  sampleTypeGroupRaw: [],
  samplesList: [],
  legLengthOptions: [],
  coreColourwayOptions: [],
  coreSizeOptions: [],
  coreLengthOptions: [],
  costingTemplateOptions: [],
  ciFactoryOptions: [],
};

/** Identifies which RowOptions list an option-bearing column should read. */
export type OptionsKey = keyof RowOptions;

/** Keys of MultiSubmitRow whose value is a boolean / string / option-array (for type-safe field params). */
export type BoolKey = { [K in keyof MultiSubmitRow]: MultiSubmitRow[K] extends boolean ? K : never }[keyof MultiSubmitRow];
export type StrKey = { [K in keyof MultiSubmitRow]: MultiSubmitRow[K] extends string ? K : never }[keyof MultiSubmitRow];
export type ArrKey = { [K in keyof MultiSubmitRow]: MultiSubmitRow[K] extends SelectOption[] ? K : never }[keyof MultiSubmitRow];

/** Page-level context keys on MultiSubmitGridContext that resolve to a `SelectOption[]`. */
export type CtxOptionsKey =
  | 'factoryOptions'
  | 'unitOptions'
  | 'sizeOptions'
  | 'lengthOptions'
  | 'supplierOptions'
  | 'costingSupplierOptions'
  | 'incotermsOptions'
;

/** All field keys of `GradingGroupLine` except `id`. */
export type GradingKey = Exclude<keyof GradingGroupLine, 'id'>;

/** Editable string field keys of `CoreProductLine` (excludes `id` and the `selected` flag). */
export type CoreKey = { [K in keyof CoreProductLine]: CoreProductLine[K] extends string ? K : never }[keyof CoreProductLine];

/** Multi-select field keys of `CoreProductLine` (Colourway / Size / Length). */
export type CoreMultiKey = { [K in keyof CoreProductLine]: CoreProductLine[K] extends string[] ? K : never }[keyof CoreProductLine];

/** Props for `CellReactSelect` — single-value react-select used inside grid cells. */
export interface CellReactSelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder: string;
  /** Borderless variant for use inside a ComboBox (e.g. UoM). */
  bare?: boolean;
  /** Red border + "required field" alert badge (e.g. supplier not chosen on submit). */
  error?: boolean;
  disabled?: boolean;
  /** Font size (px) for the control text and menu options. Defaults to 13 (grid-cell spec). */
  fontSize?: number;
  /** When true, show a clear (×) affordance so the selected value can be unset (Costing Information selects). */
  isClearable?: boolean;
}

/** Extra params for the coloured column-group header renderer. */
export interface GroupHeaderExtraParams {
  /** Show the leading group-level checkbox. */
  showCheckbox?: boolean;
  /** Optional note shown when the group is expanded, e.g. the Technical Package combine hint. */
  note?: string;
}

/** Params for the checkbox + multiselect combo cell. */
export interface CheckSelectParams {
  checkedField: BoolKey;
  valueField: ArrKey;
  optionsKey: OptionsKey;
  placeholder: string;
}

/** Params for the checkbox + fixed-label combo cell (Technical Package items). */
export interface CheckLabelParams {
  checkedField: BoolKey;
  label: string;
}

/** Params for the standalone dropdown cell (Request Sample header fields). */
export interface PlainSelectParams {
  valueField: StrKey;
  /** Inline options (legacy / static). */
  options?: SelectOption[];
  /** Per-row options key — looked up via ctx.optionsFor(rowId)[optionsKey]. */
  optionsKey?: OptionsKey;
  /** Page-level options key — read directly from grid context. */
  ctxOptionsKey?: CtxOptionsKey;
  placeholder: string;
  disabled?: boolean;
  /** When true, the control is disabled until the row has a Costing Information supplier. */
  costingGated?: boolean;
  /** When true, the dropdown offers a clear (×) affordance to unset the value (Costing Information selects). */
  clearable?: boolean;
  /** Special handler for the Sample Type Group dropdown — runs chain attachment via ctx.setSampleTypeGroup. */
  isSampleTypeGroup?: boolean;
  /** Special handler for the Sample Type dropdown — runs chain attachment via ctx.setSampleType. */
  isSampleType?: boolean;
}

/** Params for the Compose Email cell — plain input or `@`-mention recipient input. */
export interface EmailFieldParams {
  valueField: StrKey;
  placeholder: string;
  /** When true, render an `@`-mention input (To/CC/BCC). Message stays plain. */
  mention?: boolean;
}

/** Params for a grading-group column that stacks one control per grading line. */
export interface GradingStackParams {
  field: GradingKey;
  kind: 'select' | 'input';
  options?: SelectOption[];
  /** Per-row options key (e.g. 'gradingGroups'). */
  optionsKey?: OptionsKey;
  /** Page-level options key (e.g. 'sizeOptions'). */
  ctxOptionsKey?: 'sizeOptions' | 'lengthOptions' | 'unitOptions';
  placeholder: string;
}

/** Params for the plain text-input cell (Costing Information Reference / Target Cost). */
export interface TextInputParams {
  valueField: StrKey;
  placeholder: string;
  /** Render a numeric input (Target Cost). */
  numeric?: boolean;
  /** When true, the control is disabled until the row has a Costing Information supplier. */
  costingGated?: boolean;
}

/** Params for the read-only display cell (Costing Information Supplier / Currency). */
export interface DisplayCellParams {
  /** Which page-level value to display: the top-selected supplier's name, or the company currency. */
  variant: 'supplier' | 'currency';
  placeholder: string;
}

/** Params for a Core Product column that stacks one control per core line.
 *  `variant` selects the control: a select, a plain input, a `+`-prefixed quantity input,
 *  a `£`-prefixed currency input, or the trailing radio. */
export interface CoreStackParams {
  /** A `CoreMultiKey` when variant is 'select', otherwise a `CoreKey`. */
  field: CoreKey | CoreMultiKey;
  variant: 'select' | 'quantity' | 'currency';
  /** Per-row options key (e.g. 'colourwayOptions', 'legLengthOptions'). */
  optionsKey?: OptionsKey;
  /** Page-level options key (e.g. 'sizeOptions'). */
  ctxOptionsKey?: CtxOptionsKey;
  placeholder: string;
  /** When set, the control is read-only and shows quantity x pricePerUnit instead of a stored value. */
  computed?: boolean;
  /** When set, render an Invite Supplier icon beside each line's control. */
  showInvite?: boolean;
  /** Tooltip/aria label for the Invite Supplier icon (resolved translation). */
  inviteLabel?: string;
}

/** A check + multi-select cell's fill value: its checkbox and the selected option ids. */
export interface CheckedList {
  checked: boolean;
  values: string[];
}

/** One stacked line's fill value: a single id/text, or the ids of a multi-select. */
export type LineValue = string | string[];

/** What a fillable column hands AG Grid for fill, copy and paste. */
export type FillValue = boolean | string | CheckedList | LineValue[];

/** How one column reads, writes and (de)serialises its cell for fill and clipboard. */
export interface FillSpec {
  read: (row: MultiSubmitRow) => FillValue;
  apply: (value: FillValue, row: MultiSubmitRow, ctx: MultiSubmitGridContext) => void;
  toText: (value: FillValue, row: MultiSubmitRow, ctx: MultiSubmitGridContext) => string;
  fromText: (text: string, row: MultiSubmitRow, ctx: MultiSubmitGridContext) => FillValue;
  isEnabled: (row: MultiSubmitRow) => boolean;
}

/** Where a select's options come from — the same three sources the cell renderers read. */
export interface OptionSource {
  options?: SelectOption[] | undefined;
  optionsKey?: OptionsKey | undefined;
  ctxOptionsKey?: CtxOptionsKey | undefined;
}

/** Whether a row accepts a fill into a column. */
export type FillRowGate = (row: MultiSubmitRow) => boolean;

/** A fillable select's options for a target row. */
export type FillOptionsResolver = (row: MultiSubmitRow, ctx: MultiSubmitGridContext) => SelectOption[];

/** Stores a filled select value through the same path the cell uses. */
export type FillFieldWriter = (ctx: MultiSubmitGridContext, rowId: string, value: string) => void;

/** Cleans pasted or filled text; returns '' to reject it. */
export type FillTextNormalizer = (text: string) => string;

/** A stacked column's options for one line. */
export type FillLineOptions<TLine> = (line: TLine, row: MultiSubmitRow, ctx: MultiSubmitGridContext) => SelectOption[];

/** How a stacked-line column reads, validates and writes one line. */
export interface FillStackConfig<TLine extends { id: string }> {
  lines: (row: MultiSubmitRow) => TLine[];
  get: (line: TLine) => LineValue;
  optionsOf?: FillLineOptions<TLine> | undefined;
  normalize?: FillTextNormalizer | undefined;
  write: (ctx: MultiSubmitGridContext, rowId: string, line: TLine, value: LineValue) => void;
  lineEnabled?: ((line: TLine) => boolean) | undefined;
  isEnabled?: FillRowGate | undefined;
}
